import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import userService from '../../services/user.service';
import { UserType, RoleCode } from '../../types';

// ── Role → allowed user_types mapping ────────────────────────
// Helps admin pick the right combination
const roleTypeMap: Record<RoleCode, UserType> = {
  ADMIN:         'RBD_STAFF',
  MD:            'RBD_STAFF',
  PM:            'RBD_STAFF',
  SITE_ENG:      'RBD_STAFF',
  ACCOUNTS:      'RBD_STAFF',
  PURCHASE:      'RBD_STAFF',
  HR:            'RBD_STAFF',
  ESTIMATOR:     'RBD_STAFF',
  CUSTOMER:      'CUSTOMER',
  SUPPLIER:      'SUPPLIER',
  VENDOR:        'VENDOR',
  SUBCONTRACTOR: 'SUBCONTRACTOR',
};

const CreateUserPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username:  '',
    password:  '',
    full_name: '',
    email:     '',
    mobile:    '',
    user_type: 'RBD_STAFF' as UserType,
    role_code: 'PM' as RoleCode,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);

  // Auto-set user_type when role changes
  const handleRoleChange = (role: RoleCode) => {
    setForm(prev => ({
      ...prev,
      role_code: role,
      user_type: roleTypeMap[role],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!form.username || !form.password || !form.full_name) {
      setError('Username, password and full name are required.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (form.username.includes(' ')) {
      setError('Username cannot contain spaces.');
      return;
    }

    setLoading(true);
    try {
      await userService.createUser({
        username:  form.username.toLowerCase().trim(),
        password:  form.password,
        full_name: form.full_name.trim(),
        email:     form.email.trim() || undefined,
        mobile:    form.mobile.trim() || undefined,
        user_type: form.user_type,
        role_code: form.role_code,
      });

      setSuccess(true);
      setTimeout(() => navigate('/users'), 1500);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container max-w-2xl">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/users')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={18} className="text-gray-500" />
        </button>
        <div>
          <h1 className="page-title">Create User</h1>
          <p className="text-sm text-gray-500">Add a new user to RB-PMS</p>
        </div>
      </div>

      {/* Success state */}
      {success ? (
        <div className="card p-8 text-center">
          <CheckCircle2 size={48} className="text-green-500 mx-auto mb-3" />
          <p className="font-semibold text-gray-800">User created successfully!</p>
          <p className="text-sm text-gray-500 mt-1">
            Redirecting to users list...
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold text-gray-700 flex items-center gap-2">
              <UserPlus size={18} />
              User Details
            </h2>
          </div>
          <div className="card-body">

            {error && (
              <div className="alert-error mb-4">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Full Name + Username row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Rajesh Kumar"
                    value={form.full_name}
                    onChange={e => setForm({ ...form, full_name: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="form-label">Username <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. rajesh_pm"
                    value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value.toLowerCase() })}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-1">Lowercase, no spaces</p>
                </div>
              </div>

              {/* Email + Mobile row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="rajesh@rbd.in"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="form-label">Mobile</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="9876543210"
                    value={form.mobile}
                    onChange={e => setForm({ ...form, mobile: e.target.value })}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Role + User Type row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Role <span className="text-red-500">*</span></label>
                  <select
                    className="form-select"
                    value={form.role_code}
                    onChange={e => handleRoleChange(e.target.value as RoleCode)}
                    disabled={loading}
                  >
                    <optgroup label="RBD Staff">
                      {(['ADMIN','MD','PM','SITE_ENG','ACCOUNTS','PURCHASE','HR','ESTIMATOR'] as RoleCode[])
                        .map(r => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                    <optgroup label="Portal Users">
                      {(['CUSTOMER','SUPPLIER','VENDOR','SUBCONTRACTOR'] as RoleCode[])
                        .map(r => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="form-label">User Type</label>
                  <input
                    type="text"
                    className="form-input bg-gray-50"
                    value={form.user_type}
                    readOnly
                  />
                  <p className="text-xs text-gray-400 mt-1">Auto-set based on role</p>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="form-label">
                  Temporary Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input pr-10"
                    placeholder="Minimum 8 characters"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  User must change this password on first login
                </p>
              </div>

              {/* Info box */}
              <div className="alert-info text-xs">
                <span>
                  New user will be required to change their password on first login.
                  Share the username and temporary password with them securely.
                </span>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/users')}
                  className="btn-secondary flex-1 justify-center"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 justify-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus size={16} />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUserPage;